import { useTranslation } from "react-i18next";
import useFormHook from "@shared/hooks/useFormHook";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import LoadingButton from "@/shared/components/LoadingButton";
import { lengthOfServiceService } from "../services/lengthOfServiceService";
import { extractErrorMessage } from "@/shared/services/blockingError";
import { notifySuccess } from "@/config/tostifyConfig/tostifyConfig";

const FORM_ID = 'newTableRowForm';

export default function NewTableRowForm({
    onClose,
    type = 'create',
    parentRecordId,
    record = null,
    onSuccess,
}) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [years, setYears] = useState('');
    const [percent, setPercent] = useState('');
    const isEditMode = type === 'edit';

    useEffect(() => {
        if (isEditMode && record) {
            setYears(record.years ?? '');
            setPercent(record.percent ?? '');
            return;
        }

        setYears('');
        setPercent('');
    }, [isEditMode, record]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);

        if (isEditMode) {
            if (!record?.recordId) {
                setError(t('common.selectRecord'));
                return;
            }

            if (percent === '') {
                setError(t('common.required'));
                return;
            }

            setLoading(true);
            try {
                await lengthOfServiceService.updateTblClassDetailPercent(
                    record.recordId,
                    Number(percent),
                );
                notifySuccess(t('common.updatedSuccessfully'));
                onSuccess?.();
                onClose();
            } catch (submitError) {
                console.error('Error updating tbl-class detail:', submitError);
                setError(extractErrorMessage(submitError));
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!parentRecordId) {
            setError(t('common.selectRecord'));
            return;
        }

        if (years === '' || percent === '') {
            setError(t('common.required'));
            return;
        }

        setLoading(true);
        try {
            await lengthOfServiceService.createTblClassDetail({
                tblClassId: Number(parentRecordId),
                years: Number(years),
                percent: Number(percent),
            });
            notifySuccess(t('common.createdSuccessfully'));
            onSuccess?.();
            onClose();
        } catch (submitError) {
            console.error('Error creating tbl-class detail:', submitError);
            setError(extractErrorMessage(submitError));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {t('Compensations as per labor contracts.lengthOfService.secondaryTitle')}
                        </h4>
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        <form id={FORM_ID} className="region-form" onSubmit={handleSubmit}>
                            <div className="flex-column">
                                <div className="form-section width-90">
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t("Compensations as per labor contracts.lengthOfService.table.headers.yearsOfService")}
                                            {!isEditMode && <span className="required">*</span>}
                                        </span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={years}
                                            onChange={(e) => setYears(e.target.value)}
                                            min="0"
                                            readOnly={isEditMode}
                                            disabled={isEditMode}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t("Compensations as per labor contracts.lengthOfService.table.headers.percent")}
                                            <span className="required">*</span>
                                        </span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={percent}
                                            onChange={(e) => setPercent(e.target.value)}
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <LoadingButton
                            type="submit"
                            form={FORM_ID}
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? t('common.saving') : t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
